/**
 * Execute Tool Dialog
 * Allows users to test/execute a tool with custom arguments
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  PlayArrow as ExecuteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { IndividualTool } from './types';
import { useSnackbar } from 'new/contexts/SnackbarContext';

interface ExecuteToolDialogProps {
  tool: IndividualTool | null;
  open: boolean;
  onClose: () => void;
  onExecute: (toolId: number, args: Record<string, any>) => Promise<any>;
}

interface ExecutionResult {
  success: boolean;
  data?: any;
  error?: string;
  receiptId?: string;
  executionTime?: number;
}

const DEFAULT_ARGS = JSON.stringify(
  {
    format: 'dataframe_columns',
    limit: 100,
  },
  null,
  2,
);

const ExecuteToolDialog: React.FC<ExecuteToolDialogProps> = ({
  tool,
  open,
  onClose,
  onExecute,
}) => {
  const { showSuccess, showError } = useSnackbar();
  const [argsJson, setArgsJson] = useState(DEFAULT_ARGS);
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [showResult, setShowResult] = useState(true);
  const [copied, setCopied] = useState(false);

  // Reset state when dialog opens with a new tool
  useEffect(() => {
    if (open && tool) {
      setArgsJson(DEFAULT_ARGS);
      setResult(null);
      setShowResult(true);
    }
  }, [open, tool?.id]);

  const handleExecute = async () => {
    if (!tool) return;

    // Validate JSON
    let args: Record<string, any>;
    try {
      args = JSON.parse(argsJson);
    } catch (e) {
      showError('Invalid JSON format. Please check your arguments.');
      return;
    }

    setIsExecuting(true);
    setResult(null);
    const startTime = Date.now();

    try {
      const response = await onExecute(parseInt(tool.id, 10), { args });
      const executionTime = Date.now() - startTime;

      // Extract result from response
      const responseData = response?.response || response?.rsp || response;
      const receiptId = responseData?.receipt_id || responseData?.receiptId;

      setResult({
        success: true,
        data: responseData,
        receiptId,
        executionTime,
      });
      showSuccess('Tool executed successfully');
    } catch (error: any) {
      const executionTime = Date.now() - startTime;
      const errorMessage = error?.error?.message || error?.message || 'Failed to execute tool';

      setResult({
        success: false,
        error: errorMessage,
        executionTime,
      });
      showError(errorMessage);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCopyResult = () => {
    if (result?.data) {
      navigator.clipboard.writeText(JSON.stringify(result.data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (!isExecuting) {
      onClose();
    }
  };

  if (!tool) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h6">Execute Tool</Typography>
            <Chip
              label={
                tool.kind === 'nexset_read'
                  ? 'Read'
                  : tool.kind === 'nexset_action'
                    ? 'Action'
                    : 'Custom'
              }
              size="small"
              color={tool.kind === 'nexset_read' ? 'info' : 'warning'}
            />
          </Stack>
          <IconButton onClick={handleClose} disabled={isExecuting} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Tool Info */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {tool.name}
            </Typography>
            {tool.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {tool.description}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip label={`Nexset: ${tool.nexsetName}`} size="small" variant="outlined" />
              <Chip label={`ID: ${tool.id}`} size="small" variant="outlined" />
            </Stack>
          </Box>

          <Divider />

          {/* Arguments Input */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Arguments (JSON)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Provide the arguments to pass to the tool. Common options: format, limit, offset,
              filters.
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                height: 200,
              }}
            >
              <LoadableMonacoEditor
                language="json"
                value={argsJson}
                onChange={(value: string) => setArgsJson(value || '')}
                height="200px"
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  lineNumbers: 'on',
                  folding: true,
                  automaticLayout: true,
                }}
              />
            </Box>
          </Box>

          {/* Execution Result */}
          {result && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {result.success ? (
                    <SuccessIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                  <Typography variant="subtitle2">
                    {result.success ? 'Execution Successful' : 'Execution Failed'}
                  </Typography>
                  {result.executionTime && (
                    <Chip label={`${result.executionTime}ms`} size="small" variant="outlined" />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {result.success && result.data && (
                    <IconButton size="small" onClick={handleCopyResult}>
                      {copied ? (
                        <SuccessIcon fontSize="small" color="success" />
                      ) : (
                        <CopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => setShowResult(!showResult)}>
                    {showResult ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Stack>
              </Stack>

              <Collapse in={showResult}>
                {result.success ? (
                  <Box>
                    {result.receiptId && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Receipt ID: {result.receiptId}
                      </Alert>
                    )}
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        maxHeight: 300,
                      }}
                    >
                      <LoadableMonacoEditor
                        language="json"
                        value={JSON.stringify(result.data, null, 2)}
                        height="300px"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 12,
                          lineNumbers: 'off',
                          folding: true,
                          automaticLayout: true,
                          wordWrap: 'on',
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="error">{result.error}</Alert>
                )}
              </Collapse>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isExecuting}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExecute}
          disabled={isExecuting}
          startIcon={isExecuting ? <CircularProgress size={16} /> : <ExecuteIcon />}
        >
          {isExecuting ? 'Executing...' : 'Execute'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExecuteToolDialog;
